import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, Mic } from "lucide-react";
import Navbar from "../components/Navbar";
import Disclaimer from "../components/Disclaimer";
import api from "../lib/api";
import EpisodeList from "../components/podcast/EpisodeList";
import OwnerPanel from "../components/podcast/OwnerPanel";
import ShareKit from "../components/ShareKit";

export default function PodcastDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [missing, setMissing] = useState(false);
  const [episodes, setEpisodes] = useState([]);
  const [isOwner, setIsOwner] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/podcasts/${slug}`).then(({ data }) => setP(data)).catch(() => setMissing(true));
    api.get(`/podcasts/${slug}/episodes`).then(({ data }) => setEpisodes(data.episodes || [])).catch(() => {});
    api.get("/podcasts/mine").then(({ data }) => setIsOwner((data.items || []).some((i) => i.slug === slug))).catch(() => {});
  }, [slug]);

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Navbar />
      <main className="mx-auto max-w-5xl px-4 pt-6 pb-20 lg:px-8" data-testid="podcast-detail-page">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]" data-testid="podcast-back"><ArrowLeft size={16} /> Back</button>
        {missing && <p className="mt-10 text-[var(--text-secondary)]" data-testid="podcast-not-found">This podcast isn't on The Dial.</p>}
        {p && (
          <div className="mt-6 flex flex-col gap-8 sm:flex-row">
            <div className="w-full max-w-[280px] flex-none self-center sm:self-start">
              {p.poster ? (
                <img src={p.poster} alt={p.title} className="aspect-square w-full rounded-[var(--card-radius)] border border-[var(--border-subtle)] object-cover shadow-xl" data-testid="podcast-cover" />
              ) : (
                <div className="flex aspect-square w-full items-center justify-center rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] text-[var(--text-muted)]"><Mic size={48} /></div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <span className="inline-block rounded-sm bg-[var(--accent)] px-2 py-0.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[var(--accent-contrast)]" data-testid="podcast-badge">Podcast</span>
              <h1 className="mt-3 font-head text-4xl sm:text-5xl tracking-wide text-[var(--text-primary)] break-words" data-testid="podcast-title">{p.title}</h1>
              {p.host && <p className="mt-2 text-sm text-[var(--text-secondary)]" data-testid="podcast-host">Hosted by <span className="font-semibold text-[var(--text-primary)]">{p.host}</span></p>}
              <p className="mt-5 max-w-2xl whitespace-pre-wrap break-words text-base leading-relaxed text-[var(--text-secondary)]" data-testid="podcast-description">{p.description}</p>
              <a href={p.url} target="_blank" rel="noopener noreferrer" className="mt-7 inline-flex items-center gap-2 rounded-[var(--card-radius)] bg-[var(--accent)] px-6 py-3 font-bold uppercase tracking-wide text-[var(--accent-contrast)] transition-colors hover:bg-[var(--accent-hover)]" data-testid="podcast-listen-link">
                <ExternalLink size={18} /> {p.url_label}
              </a>
              <p className="mt-3 text-xs text-[var(--text-muted)]">Opens the show's own page — The Dial doesn't host podcasts.</p>
              <ShareKit kind="podcasts" slug={slug} title={p.title} className="mt-4" />
            </div>
          </div>
        )}
        {p && episodes.length > 0 && (
          <section className="mt-12">
            <h2 className="font-head text-2xl text-[var(--text-primary)]">Episodes</h2>
            <EpisodeList episodes={episodes} />
          </section>
        )}
        {p && isOwner && <OwnerPanel slug={slug} />}
      </main>
      <Disclaimer />
    </div>
  );
}
